import { parallel as asyncParallel, each as asyncEach, series as asyncSeries } from "async";
import { BaseService } from "../baseService";
import { StorageClient } from "../../clients/storageClient";
import { RouterClient } from "../../clients/routerClient";
import { Logger } from "../../clients/logger";
import { ConfigClient } from "../../clients/configClient";
import SystemManagerClient from "../../common/systemManagerClient";
import { StandardCallback } from "../../types";

const STORAGE_TOPIC = "finsemble";
const USER_PREFERENCES_STORAGE_KEY = "userPreferences";

type UserPreferences = Record<string, any>;

class PreferenceService extends BaseService {
	userPreferences: UserPreferences = {};

	/**
	 * This flag flips to true when the service is ready. Any client calls that are received (through the router listeners)
	 * prior to the service being ready will result in errors in the central logger. This should never happen since Finsemble's dependencies
	 * are structured to prevent this.
	 */
	serviceReady: boolean = false;

	/**
	 * @param {string} params.name name of the service
	 * @param {object} params.startupDependencies
	 * @param {array} params.startupDependencies.clients Clients to wait on before initializing the service
	 * @param {array} params.startupDependencies.services Services to wait on before initializing the service
	 * @param {object} params.shutdownDependencies
	 * @param {array} params.shutdownDependencies.services Services to wait on before shutting down the service
	 * */
	constructor() {
		super({
			name: "preferencesService",
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});

		this.onBaseServiceReady((cb: () => void) => {
			this.doInit(cb);
		});
		this.start();
	}

	/**
	 * Creates router endpoints for all of our client APIs.
	 * @private
	 */
	createRouterEndpoints() {
		RouterClient.addResponder("PreferencesService.getPreferences", (err, message) => {
			this.serviceReady || Logger.system.error("preferencesService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}
			message.sendQueryResponse(err, this.userPreferences);
		});

		RouterClient.addResponder("PreferencesService.setPreference", (err, message) => {
			this.serviceReady || Logger.system.error("preferencesService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}

			const preference = message.data;
			this.setPreference(preference, (err2) => {
				message.sendQueryResponse(err2, err2 ? null : "success");
			});
		});

		RouterClient.addResponder("PreferencesService.unsetPreference", (err, message) => {
			this.serviceReady || Logger.system.error("preferencesService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}

			const params = message.data;
			this.unsetPreference(params, (err2) => {
				message.sendQueryResponse(err2, err2 ? null : "success");
			});
		});
	}

	doInit(cb: () => void) {
		const routerEndpoints = (done: () => void) => {
			this.createRouterEndpoints();
			done();
		};

		const getPreferences = (done: () => void) => {
			this.initiateRetrievingPreferences(done);
		};

		const updateDefaultPreferences = (done: () => void) => {
			this.updateDefaultPreferences(done);
		};

		const setPreferences = (done: () => void) => {
			this.setPreferencesOnConfigStore(done);
		};

		SystemManagerClient.waitForBootStage("authentication", "stageCompleted", () => {
			asyncSeries([routerEndpoints, getPreferences, updateDefaultPreferences, setPreferences], () => {
				this.serviceReady = true;
				cb();
			});
		});
	}

	/**
	 * Returns the userPreferences object that's saved on the service.
	 * @param {function} cb
	 */
	getPreferences(cb: StandardCallback) {
		cb(null, this.userPreferences);
	}

	/**
	 * Retrieves preferences from storage and caches them on the service.
	 * Removes old configuration parameters that are no longer used before caching.
	 *
	 * @param {*} done
	 */
	async initiateRetrievingPreferences(callback: () => void) {
		Logger.system.debug("initiateRetrievingPreferences");
		SystemManagerClient.waitForStartup("storageService", () => {
			StorageClient.get({ topic: STORAGE_TOPIC, key: USER_PREFERENCES_STORAGE_KEY }, (err, data) => {
				Logger.system.debug("initiateRetrievingPreferences", data);
				if (err) {
					Logger.system.error(err);
				}
				if (data) {
					try {
						delete data["finsemble.components.Toolbar.window.dockable"];
						delete data["finsemble.components.Toolbar.window.options.resizable"];
						delete data["finsemble.components.Toolbar.window.monitor"];
					} catch (e) {}
					this.userPreferences = data;
					Logger.system.log("APPLICATION LIFECYCLE:User Preferences Retrieved", data);
				} else {
					Logger.system.log("APPLICATION LIFECYCLE:No user preferences found");
				}
				callback();
			});
		});
	}

	/**
	 * API Endpoint; given a path and a value, calls setValue on the configClient and saves the data to storage.
	 * @param {object} params
	 * @param {string} params.field which preference to set (e.g., "finsemble.services.docking...")
	 * @param {any} params.value value of the preference.
	 * @param {*} cb
	 */
	setPreference(params: { field: string; value: any }, cb: StandardCallback) {
		const setValueInStore = (done: () => void) => {
			ConfigClient.setValue(params, done);
		};

		const persistValue = (done: () => void) => {
			this.userPreferences[params.field] = params.value;
			StorageClient.save(
				{
					topic: STORAGE_TOPIC,
					key: USER_PREFERENCES_STORAGE_KEY,
					value: this.userPreferences,
				},
				(err) => {
					if (err) {
						Logger.system.error(err);
					}
					done();
				}
			);
		};

		// Set the value in the config store and persist the value to storage. When both are done, invoke the callback telling the user that all is well - or not. Error gets passed back too.
		asyncParallel([setValueInStore, persistValue], () => {
			cb(null);
		});
	}

	/**
	 * API Endpoint; given a path, removes a value set on the configStore and persists that removal to storage.
	 * @param {object} params
	 * @param {string} params.field which preference to unset (e.g., "finsemble.services.docking...")
	 * @param {*} cb
	 */
	unsetPreference(params: { field: string }, cb: StandardCallback) {
		const setValueInStore = (done: () => void) => {
			ConfigClient.removeValue(params.field, done);
		};

		const persistValue = (done: () => void) => {
			delete this.userPreferences[params.field];
			StorageClient.save(
				{
					topic: STORAGE_TOPIC,
					key: USER_PREFERENCES_STORAGE_KEY,
					value: this.userPreferences,
				},
				(err) => {
					if (err) {
						Logger.system.error(err);
					}
					done();
				}
			);
		};

		// Set the value in the config store and persist the value to storage. When both are done, invoke the callback telling the user that all is well - or not. Error gets passed back too.
		asyncParallel([setValueInStore, persistValue], () => {
			cb(null);
		});
	}

	/**
	 * Updates user preferences from config
	 * @private
	 */
	async updateDefaultPreferences(cb: () => void) {
		// preferences to update from config
		const scheduledRestart = "finsemble.scheduledRestart";
		const scheduledShutdown = "finsemble.scheduledShutdown";
		const scheduledCloseDiscardUnsavedChanges = "finsemble.scheduledCloseDiscardUnsavedChanges";

		const { data: config } = await ConfigClient.getValues([
			scheduledRestart,
			scheduledShutdown,
			scheduledCloseDiscardUnsavedChanges,
		]);

		if (config) {
			if (this.userPreferences[scheduledRestart] === undefined && !!config[scheduledRestart]) {
				this.userPreferences[scheduledRestart] = config[scheduledRestart];
			}

			if (this.userPreferences[scheduledShutdown] === undefined && !!config[scheduledShutdown]) {
				this.userPreferences[scheduledShutdown] = config[scheduledShutdown];
			}

			if (
				this.userPreferences[scheduledCloseDiscardUnsavedChanges] === undefined &&
				!!config[scheduledCloseDiscardUnsavedChanges]
			) {
				this.userPreferences[scheduledCloseDiscardUnsavedChanges] = config[scheduledCloseDiscardUnsavedChanges];
			}
		}

		cb();
	}

	/**
	 * Takes the preferences that were retrieved from storage, and sets them in the configStore.
	 * @param {callback} done
	 */
	setPreferencesOnConfigStore(cb: () => void) {
		const setPreferenceOnConfig = (field: string, done: () => void) => {
			const preference = {
				field,
				value: this.userPreferences[field],
			};
			ConfigClient.setValue(preference, () => {
				done();
			});
		};

		Logger.system.debug("PreferenceService: setPreferencesOnConfigStore");
		const preferences = Object.keys(this.userPreferences);
		if (preferences.length) {
			asyncEach(preferences, setPreferenceOnConfig, cb);
		} else {
			cb();
		}
	}
}

export default new PreferenceService();
